/**
 * SSR-safe variant of the B2C homepage rendered through the marketing
 * SSR pipeline (`MARKETING_ROUTES` → `marketingSsrHandler`).
 *
 * Differences vs `Home.tsx`:
 *   - No `useAuthState` / `useUser` / `useBusinessContext` / `useOrganisation`
 *     so the page renders cleanly under StaticRouter without provider plumbing.
 *   - Signed-in visitor redirect (to `/home` or `/onboarding`) is handled
 *     in a client-only effect that lazy-loads the firebase module after
 *     hydration. SSR output never imports firebase.
 *   - The B2B early-return (`<StipplBusinessLanding>`) is dropped — the B2B
 *     `/` route is mapped separately in `marketingRoutes.tsx`.
 */

import { useApolloClient } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { OsTypes, osName } from "react-device-detect";
import "tippy.js/animations/scale.css";
import "tippy.js/dist/tippy.css";
import "../css/Website.scss";

import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { t } from "i18n/index";
import { discoverPath } from "i18n/urlLocale";
import { USER } from "graphql/queries/user";
import BusinessWaveBanner from "../components/BusinessWaveBanner";
import ClosingCTASection from "../components/ClosingCTASection";
import CommunitySection from "../components/CommunitySection";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import FeatureToolboxSection from "../components/FeatureToolboxSection";
import Footer from "../components/Footer";
import HeroSearchCard, { type HeroSearchTab } from "../components/HeroSearchCard";
import HeroShowcase from "../components/HeroShowcase";
import { replaceWithAppRoute } from "../components/navigateToApp";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Navigation from "../components/navigation/Navigation";
import TestimonialsSection from "../components/TestimonialsSection";
import TrendingDestinationsSection from "../components/TrendingDestinationsSection";
import type { HomepagePicks } from "components/website/components/TrendingDestinationsSection";
import { getHomeTranslations } from "../marketing/i18n/pages/home";
import { POPULAR_DESTINATIONS, localizedDestinationLabel } from "./Home";

const LatestArticlesSection = React.lazy(() => import("../components/LatestArticlesSection"));

// Decorative hero social-proof avatars — placeholder stock photos, not real users.
const HERO_AVATARS = [
	"1548213238-0da7521bd6e0",
	"1637692513738-68ed61f6604a",
	"1612000649051-611809293050",
	"1742783106217-7204748c6874",
	"1578955682343-97ea7f7464da",
];

// Defers mounting children until the placeholder div is near the viewport.
// Prevents below-the-fold sections from executing requests on page load.
const LazyMount: React.FC<{ children: React.ReactNode; rootMargin?: string }> = ({ children, rootMargin = "200px" }) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [visible, setVisible] = React.useState(false);
	React.useEffect(() => {
		if (typeof IntersectionObserver === "undefined") {
			setVisible(true);
			return undefined;
		}
		const obs = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					obs.disconnect();
				}
			},
			{ rootMargin }
		);
		if (ref.current) obs.observe(ref.current);
		return () => obs.disconnect();
	}, [rootMargin]);
	return <div ref={ref}>{visible ? children : null}</div>;
};

interface HomeMarketingProps {
	lang?: string;
	/**
	 * From the `readyGeos` loader: the geos that have a /discover page, and the two curated rows
	 * from `stippl-knowledge/copy/_homepage.md`. See TrendingDestinationsSection — the picks are
	 * optional and it falls back to its built-in list without them.
	 */
	initialData?: { readyGeos?: { geo: string }[]; homepagePicks?: HomepagePicks };
}

const HomeMarketing: React.FC<HomeMarketingProps> = ({ lang, initialData }) => {
	const apollo = useApolloClient();
	const translations = getHomeTranslations(lang || "en");
	const [heroSearchTab, setHeroSearchTab] = useState<HeroSearchTab>("plan");

	// The hero subtitle is one paragraph on desktop; on a phone it breaks into its three
	// sentences — "what Stippl is", "what it does", then the social-proof stats — so the AI
	// box below it isn't pushed as far down (86cb93wpg). Split on sentence boundaries rather
	// than hard-coding three keys, so every locale keeps working from the one string.
	const heroSubtitle = t(
		translations,
		"home.hero.subtitle",
		"Stippl is your free AI travel planner. Describe your dream trip and the AI builds a full day-by-day itinerary: budget, stays and bookings, all in one app."
	);
	const heroSubtitleSentences = heroSubtitle.split(". ").map((s, i, arr) => (i < arr.length - 1 ? `${s}.` : s));
	const heroSubtitleLead = heroSubtitleSentences[0];
	const heroSubtitleStats = heroSubtitleSentences.length > 2 ? heroSubtitleSentences.slice(-2).join(" ") : "";
	const heroSubtitleBody = heroSubtitleSentences.slice(1, heroSubtitleStats ? -2 : undefined).join(" ");

	useEffect(() => {
		// A visitor who was already signed in when `/` loaded goes into the app: `/home`, or
		// `/onboarding` without a username, as Login.tsx decides. Client-side so SSR never pulls
		// firebase in. Only the first auth emission counts: a sign-in in the plan-gate modal during
		// the visit is the modal's to route (navigateToApp, or the stay-search resume), not ours.
		// The profile comes from the GraphQL USER query, not REST /api/user: should it not exist yet,
		// GraphQL getUser is the creation path that merges stashed attribution and fires
		// user_created (123yb6924v2).
		let cancelled = false;
		let unsub: (() => void) | undefined;
		void (async () => {
			try {
				const firebaseModule = await import("../../firebase");
				const auth = firebaseModule.auth;
				if (!auth || cancelled) return;
				let first = true;
				unsub = auth.onAuthStateChanged((fbUser) => {
					if (!first) return;
					first = false;
					// Anonymous sessions aren't offered today; should they return, they stay on this page.
					if (cancelled || !fbUser || fbUser.isAnonymous) return;
					void (async () => {
						try {
							const { data } = await apollo.query({ query: USER, fetchPolicy: "network-only" });
							if (cancelled || !data?.user) return;
							const username = data.user.username;
							replaceWithAppRoute(username && !username.includes("StipplUser") ? "/home" : "/onboarding");
						} catch {
							// best-effort — silent on failure, marketing page stays visible
						}
					})();
				});
			} catch {
				// firebase failed to load — leave marketing page as-is
			}
		})();
		return () => {
			cancelled = true;
			unsub?.();
		};
	}, [apollo]);

	const handleSignupTracking = (location: string) => {
		if (osName === OsTypes.Android) {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: location }, "marketing_site");
		} else if (osName === OsTypes.IOS) {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: location }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: location }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: always the attributed OneLink now, mobile or desktop -- see the hook's doc
	// comment in useAppStoreLink.ts.
	const appstoreUrl = useAppDownloadUrl();

	return (
		<div id="stippl-homepage">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />
			<main>
				<section className="intro-header">
					<div className="container__inner">
						<div className="intro-header__grid">
							<div className="intro-header__content">
								<a
									href={appstoreUrl}
									className="intro-header__get-app"
									onClick={() => handleSignupTracking("homepage_get_app_pill")}
									target="_blank"
									rel="noreferrer"
								>
									<span className="intro-header__get-app-icon">
										<Icon name="stippl" size={15} fill="white" />
									</span>
									{t(translations, "home.hero.ctaMobile", "Get the app")}
								</a>

								<div className="intro-header__social-proof">
									<div className="intro-header__avatars">
										{HERO_AVATARS.map((id) => (
											<img key={id} src={`https://images.unsplash.com/photo-${id}?crop=faces&fit=crop&fm=jpg&q=80&w=80&h=80`} alt="" loading="lazy" />
										))}
									</div>
									<div>
										<div className="intro-header__stars">
											{[0, 1, 2, 3, 4].map((i) => (
												<Icon key={i} name="star--filled" size={12} fill="#f5a623" />
											))}
										</div>
										<span>
											{t(translations, "home.hero.trustPrefix", "Joined by")} <strong>{t(translations, "home.hero.trustText", "1.5M+ travelers")}</strong>
										</span>
									</div>
								</div>

								<span className="intro-header__eyebrow">
									<Icon name="sparkle-ai" size={13} />
									{t(translations, "home.hero.eyebrow", "Meet your AI travel planner")}
								</span>

								<h1>
									{t(translations, "home.hero.titleLine1", "Where to next?")}
									<br />
									{t(translations, "home.hero.titleLine2Prefix", "Let's ")}
									<span className="intro-header__accent">{t(translations, "home.hero.titleLine2Highlight", "plan")}</span>
									{t(translations, "home.hero.titleLine2Suffix", " it.")}
								</h1>

								<p className="intro-p">
									{heroSubtitleLead}
									{heroSubtitleBody && (
										<>
											{" "}
											<br className="intro-p__br" />
											{heroSubtitleBody}
										</>
									)}
									{heroSubtitleStats && (
										<>
											{" "}
											<br className="intro-p__br" />
											{heroSubtitleStats}
										</>
									)}
								</p>

								<HeroSearchCard onTabChange={setHeroSearchTab} />

								{heroSearchTab !== "explore" && (
									<div className="intro-header__popular">
										<span>Popular:</span>
										{/* Localised, same as the Footer and the client Home (123yb69329x). This is the
										    SSR variant — the markup a crawler and a cold first paint actually get — so an
										    English label here is the one that gets indexed on /es and /nl. */}
										{POPULAR_DESTINATIONS.map((d) => (
											<a href={discoverPath(`/discover/${d.geo}`, lang || "en")} key={d.geo}>
												{localizedDestinationLabel(d, lang || "en")}
											</a>
										))}
									</div>
								)}
							</div>

							<HeroShowcase />
						</div>
					</div>
				</section>

				<TrendingDestinationsSection readyGeos={initialData?.readyGeos} picks={initialData?.homepagePicks} />

				<FeatureToolboxSection />

				<CommunitySection />

				<TestimonialsSection variant="homepage" />

				<BusinessWaveBanner />

				<LazyMount>
					<React.Suspense fallback={null}>
						<LatestArticlesSection picks={initialData?.homepagePicks} />
					</React.Suspense>
				</LazyMount>

				<ClosingCTASection />
			</main>
			<Footer />
			<DownloadAppQrCard />
		</div>
	);
};

export default HomeMarketing;
